import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// material-ui
import { Alert, Box, Breadcrumbs, Button, Chip, CircularProgress, CssBaseline, Divider, Grid, IconButton, InputAdornment, Link, Stack, TextField, Typography } from '@mui/material';
import { Archive, Clear, Edit, LockPersonOutlined, Search, Unarchive } from '@mui/icons-material/';

//components
import TopBar from '../../components/nav/TopBar';
import KnowledgeTreeView from './components/KnowledgeTreeView';
import KnowledgeSearchResults from './components/KnowledgeSearchResults';
import NewNoteDialog from './components/NewNoteDialog';
import AccessDialog, { AccessTarget } from './components/AccessDialog';
import { sendJson } from './knowledgeFetch';
import { KnowledgeNote, KnowledgeTree, NoteLock, SearchResult } from './dataObjects/KnowledgeTypes';
import dayjs from 'dayjs';
import './knowledge.css';

// The editor (Milkdown, a few hundred kB) loads only when someone opens it, not with every Extranet app page.
const NoteEditPanel = React.lazy(() => import('./components/NoteEditPanel'));

const lockText = (lock: NoteLock): string =>
    `${lock.name} is editing this note (since ${dayjs(lock.since).format('h:mm A')}).`;

const BASE = '/apps/knowledge/';

/** Vault path -> app URL, one segment at a time so the slashes stay. */
const urlFor = (path: string, heading?: string | null): string =>
    BASE + path.split('/').map(encodeURIComponent).join('/') + (heading ? '#' + encodeURIComponent(heading) : '');

/** App URL -> vault path ('' for the area's home). */
const pathFrom = (pathname: string): string => {
    const rest = pathname.toLowerCase().startsWith(BASE) ? pathname.substring(BASE.length) : '';
    return rest.split('/').filter(s => s).map(s => decodeURIComponent(s)).join('/');
};

/** Who a note is visible to, in words. */
const audienceOf = (access: string[]): string => access.map(a => {
    if (a === 'org') return 'All staff';
    if (a === 'it') return 'IT';
    if (a.startsWith('dept:')) return 'Dept: ' + a.substring(5);
    if (a.startsWith('user:')) return 'One person';
    return a;
}).join(', ');

/** Fetch JSON from /Knowledge/*; throws a message fit to show. */
const getJson = async <T,>(url: string): Promise<T> => {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    const type = response.headers.get('content-type') ?? '';
    if (response.redirected || (response.ok && !type.includes('json'))) {
        // The session ran out and the sign-in page came back instead.
        throw new Error('Your session has ended. Reload the page to sign in again.');
    }
    if (response.status === 404) {
        throw new Error('not-found');
    }
    if (!response.ok) {
        throw new Error((await response.text()) || response.statusText);
    }
    return response.json();
};

const KnowledgeApp = (): JSX.Element => {

    //set up browser title
    const applicationName = 'Knowledge';

    const location = useLocation();
    const navigate = useNavigate();
    const currentPath = pathFrom(location.pathname);
    const contentRef = useRef<HTMLDivElement>(null);

    const [tree, setTree] = useState<KnowledgeTree>();
    const [treeError, setTreeError] = useState<string>();
    const [note, setNote] = useState<KnowledgeNote>();
    const [noteError, setNoteError] = useState<string>();
    const [isLoadingNote, setIsLoadingNote] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<SearchResult[]>();
    const [isSearching, setIsSearching] = useState(false);
    const [editing, setEditing] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [newNoteFolder, setNewNoteFolder] = useState<string | null>(null);
    const [accessTarget, setAccessTarget] = useState<AccessTarget | null>(null);
    const [actionError, setActionError] = useState<string>();
    const [isArchiving, setIsArchiving] = useState(false);
    // A just-created note opens straight into the editor.
    const editAfterLoad = useRef<string | null>(null);

    const open = useCallback((path: string, heading?: string | null) => {
        navigate(urlFor(path, heading));
    }, [navigate]);

    //payload: the tree, again after a save or a new note
    useEffect(() => {
        getJson<KnowledgeTree>('/Knowledge/Tree')
            .then(setTree)
            .catch((error: Error) => setTreeError(error.message === 'not-found'
                ? 'The Knowledge area is not available.'
                : error.message));
    }, [reloadKey]);

    //leaving a note leaves its editor (which releases the edit lease)
    useEffect(() => {
        setEditing(editAfterLoad.current === currentPath);
        editAfterLoad.current = null;
    }, [currentPath]);

    //the note named by the URL, or the home note
    useEffect(() => {
        const path = currentPath || tree?.home;
        if (!path) {
            setNote(undefined);
            return;
        }
        setIsLoadingNote(true);
        setNoteError(undefined);
        getJson<KnowledgeNote>('/Knowledge/Note?path=' + encodeURIComponent(path))
            .then(result => {
                setNote(result);
                document.title = 'Extranet - ' + result.title;
            })
            .catch((error: Error) => {
                setNote(undefined);
                setNoteError(error.message === 'not-found' ? 'This note does not exist, or you do not have access to it.' : error.message);
            })
            .finally(() => setIsLoadingNote(false));
    }, [currentPath, tree?.home, reloadKey]);

    //scroll to the heading in the URL's hash once the note is on the page
    useEffect(() => {
        const container = contentRef.current;
        if (!note || !container) {
            return;
        }
        const wanted = decodeURIComponent(location.hash.replace(/^#/, ''));
        if (!wanted) {
            window.scrollTo(0, 0);
            return;
        }
        const headings = Array.from(container.querySelectorAll('h1, h2, h3, h4, h5, h6')) as HTMLElement[];
        const target = headings.find(h => h.id === wanted)
            ?? headings.find(h => (h.textContent ?? '').trim().toLowerCase() === wanted.trim().toLowerCase());
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [note, location.hash]);

    //links inside the note: in-app navigation for notes, the browser for everything else
    const onContentClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const anchor = (event.target as HTMLElement).closest('a') as HTMLAnchorElement | null;
        if (!anchor || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey) {
            return;
        }
        const notePath = anchor.getAttribute('data-note');
        if (notePath) {
            event.preventDefault();
            const hash = anchor.hash ? decodeURIComponent(anchor.hash.substring(1)) : null;
            open(notePath, hash);
        }
        else if (anchor.getAttribute('href')?.startsWith('#')) {
            event.preventDefault();
            navigate({ pathname: location.pathname, hash: anchor.getAttribute('href') ?? '' });
        }
    };

    const search = () => {
        const q = query.trim();
        if (!q) {
            setResults(undefined);
            return;
        }
        setIsSearching(true);
        getJson<{ results: SearchResult[] }>('/Knowledge/Search?q=' + encodeURIComponent(q))
            .then(result => setResults(result.results))
            .catch((error: Error) => {
                setResults([]);
                setNoteError(error.message);
            })
            .finally(() => setIsSearching(false));
    };

    const clearSearch = () => {
        setQuery('');
        setResults(undefined);
    };

    //archive takes the note out of search and Cappy; it stays readable here
    const setArchived = async (archived: boolean) => {
        if (!note) {
            return;
        }
        setIsArchiving(true);
        setActionError(undefined);
        try {
            const reply = await sendJson<object>('/Knowledge/Archive', 'POST', { path: note.path, archived });
            if (reply.status === 200) {
                setReloadKey(k => k + 1);
            }
            else {
                setActionError(reply.data.error ?? `That didn't work (${reply.status}).`);
            }
        }
        catch {
            setActionError("That didn't work. Try again in a minute.");
        }
        finally {
            setIsArchiving(false);
        }
    };

    const crumbs = note ? note.path.split('/') : [];

    return (
        <div>
            <Grid container sx={{ display: 'flex' }}>
                <CssBaseline />
                <TopBar applicationName={applicationName}></TopBar>
            </Grid>
            <Grid item xs={12}>
                <Divider />
            </Grid>
            {treeError ?
                <Alert severity="warning" sx={{ m: 4 }}>{treeError}</Alert>
                :
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    {/* sidebar: search and tree */}
                    <Box className="kb-sidebar" sx={{ width: 330, flexShrink: 0, borderRight: 1, borderColor: 'divider' }}>
                        <Box sx={{ p: 1.5 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search the knowledge base"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                                    endAdornment: (query || results) &&
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={clearSearch} aria-label="Clear search"><Clear fontSize="small" /></IconButton>
                                        </InputAdornment>
                                }}
                            />
                        </Box>
                        {isSearching && <Box sx={{ p: 2 }}><CircularProgress size={22} /></Box>}
                        {!isSearching && results &&
                            <KnowledgeSearchResults results={results} onOpen={(path, heading) => open(path, heading)} />
                        }
                        {!results && !tree && <Box sx={{ p: 2 }}><CircularProgress size={22} /></Box>}
                        {!results && !isSearching && tree &&
                            <KnowledgeTreeView root={tree.root} currentPath={note?.path ?? currentPath} onOpen={(path) => open(path)}
                                personalFolder={tree.personal}
                                onCreate={tree.canEdit ? (folder) => setNewNoteFolder(folder) : undefined}
                                onFolderAccess={tree.isIt && tree.canEdit ? (folder) => setAccessTarget({ kind: 'folder', path: folder }) : undefined} />
                        }
                    </Box>

                    {/* the note */}
                    <Box sx={{ flexGrow: 1, minWidth: 0, px: 5, py: 3 }}>
                        {noteError && <Alert severity="info" sx={{ mb: 2 }}>{noteError}</Alert>}
                        {isLoadingNote && !note && <CircularProgress size={28} />}
                        {!note && !noteError && !isLoadingNote && tree &&
                            <Typography variant="body1" color="text.secondary">Pick a note on the left, or search.</Typography>
                        }
                        {note &&
                            <Box sx={{ maxWidth: 960, opacity: isLoadingNote ? 0.5 : 1 }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                                    <Breadcrumbs maxItems={5} sx={{ fontSize: '0.85rem' }}>
                                        {crumbs.map((part, i) =>
                                            <Typography key={i} variant="caption" color={i === crumbs.length - 1 ? 'text.primary' : 'text.secondary'}>
                                                {i === crumbs.length - 1 ? part.replace(/\.md$/i, '') : part}
                                            </Typography>
                                        )}
                                    </Breadcrumbs>
                                    <Stack direction="row" spacing={1}>
                                        {note.archived &&
                                            <Chip size="small" color="warning" icon={<Archive fontSize="small" />}
                                                label={'Archived' + (note.archived.on ? ' ' + note.archived.on : '')}
                                                title={"Not in search or Cappy's answers." + (note.archived.by ? ' Archived by ' + note.archived.by + '.' : '')} />
                                        }
                                        <Chip size="small" variant="outlined" label={'Visible to: ' + audienceOf(note.access)} />
                                    </Stack>
                                </Stack>
                                {!editing && note.canEdit &&
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                        {note.lock && !note.lock.mine
                                            ? <Alert severity="info" sx={{ py: 0 }}>{lockText(note.lock)} You can read it; editing opens up when they finish.</Alert>
                                            : <>
                                                <Button size="small" variant="outlined" startIcon={<Edit fontSize="small" />} onClick={() => setEditing(true)}>Edit</Button>
                                                <Button size="small" variant="outlined" color="inherit" disabled={isArchiving}
                                                    startIcon={note.archived ? <Unarchive fontSize="small" /> : <Archive fontSize="small" />}
                                                    onClick={() => setArchived(!note.archived)}>
                                                    {note.archived ? 'Unarchive' : 'Archive'}
                                                </Button>
                                                {tree?.isIt &&
                                                    <Button size="small" variant="outlined" color="inherit" startIcon={<LockPersonOutlined fontSize="small" />}
                                                        onClick={() => setAccessTarget({ kind: 'note', path: note.path, current: note.access })}>
                                                        Who can see this
                                                    </Button>
                                                }
                                            </>
                                        }
                                    </Stack>
                                }
                                {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(undefined)}>{actionError}</Alert>}
                                {editing
                                    ? <React.Suspense fallback={<CircularProgress size={28} />}>
                                        <NoteEditPanel key={note.path} path={note.path} onDone={(saved) => {
                                            setEditing(false);
                                            if (saved) {
                                                setReloadKey(k => k + 1);
                                            }
                                        }} />
                                    </React.Suspense>
                                    : <div
                                        ref={contentRef}
                                        className="kb-note"
                                        onClick={onContentClick}
                                        // Rendered by the Hub with raw HTML disabled and only http/https/mailto links kept.
                                        dangerouslySetInnerHTML={{ __html: note.html }}
                                    />
                                }
                                {!editing && note.backlinks.length > 0 &&
                                    <Box sx={{ mt: 5, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Linked from</Typography>
                                        <Stack spacing={0.5}>
                                            {note.backlinks.map(link =>
                                                <Link key={link.path} href={urlFor(link.path)} variant="body2" underline="hover"
                                                    onClick={(e: React.MouseEvent) => { e.preventDefault(); open(link.path); }}>
                                                    {link.title}
                                                </Link>
                                            )}
                                        </Stack>
                                    </Box>
                                }
                            </Box>
                        }
                    </Box>
                </Box>
            }
            <AccessDialog
                target={accessTarget}
                onClose={() => setAccessTarget(null)}
                onSaved={() => {
                    setAccessTarget(null);
                    setReloadKey(k => k + 1);
                }}
            />
            <NewNoteDialog
                folder={newNoteFolder}
                folderLabel={newNoteFolder === tree?.personal ? 'My notes (only you can see them)' : (newNoteFolder ?? '')}
                onClose={() => setNewNoteFolder(null)}
                onCreated={(path) => {
                    setNewNoteFolder(null);
                    setReloadKey(k => k + 1);
                    editAfterLoad.current = path;
                    open(path);
                }}
            />
        </div>
    );
};

export default KnowledgeApp;
